import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/EditOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { penaltyCatalogApi, type SavePenaltyCatalogRequest } from '../../api/penaltyCatalogApi';
import {
  PENALTY_CATEGORY_LABELS,
  type PenaltyCatalogItem,
  type PenaltyCategory,
} from '../../types/results';
import type { Category } from '../../types/domain';

const CATEGORY_ORDER: PenaltyCategory[] = ['OBSTACLES', 'HOSE', 'COUPLING', 'EQUIPMENT', 'OTHER', 'CUSTOM'];

interface Props {
  competitionId: number;
  categories: Category[];
}

export const CompetitionPenaltyCatalogTab = ({ competitionId, categories }: Props) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [disciplineFilter, setDisciplineFilter] = useState<number | 'all'>('all');
  const [editing, setEditing] = useState<PenaltyCatalogItem | null>(null);
  const [creating, setCreating] = useState(false);

  const allDisciplines = useMemo(
    () =>
      categories.flatMap((c) =>
        (c.disciplines ?? []).map((d) => ({ id: d.id, name: d.name, categoryName: c.name, type: d.type }))
      ),
    [categories]
  );

  const ctifDisciplines = allDisciplines.filter((d) => d.type === 'POZARNI_UTOK_CTIF');

  const listQuery = useQuery({
    queryKey: ['penalty-catalog', competitionId, 'all'],
    queryFn: () => penaltyCatalogApi.list(competitionId),
  });
  const items = listQuery.data ?? [];

  const filtered = useMemo(() => {
    if (disciplineFilter === 'all') return items;
    return items.filter((it) => it.disciplineId === disciplineFilter || it.disciplineId == null);
  }, [items, disciplineFilter]);

  const grouped = useMemo(() => {
    const m = new Map<PenaltyCategory, PenaltyCatalogItem[]>();
    for (const it of filtered) {
      const arr = m.get(it.category) ?? [];
      arr.push(it);
      m.set(it.category, arr);
    }
    return m;
  }, [filtered]);

  const updateMutation = useMutation({
    mutationFn: (vars: { id: number; req: SavePenaltyCatalogRequest }) =>
      penaltyCatalogApi.update(competitionId, vars.id, vars.req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['penalty-catalog'] });
      enqueueSnackbar('Uloženo', { variant: 'success' });
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba uložení', { variant: 'error' }),
  });

  const createMutation = useMutation({
    mutationFn: (req: SavePenaltyCatalogRequest) => penaltyCatalogApi.create(competitionId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['penalty-catalog'] });
      enqueueSnackbar('Vytvořeno', { variant: 'success' });
      setCreating(false);
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba vytvoření', { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => penaltyCatalogApi.delete(competitionId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['penalty-catalog'] });
      enqueueSnackbar('Smazáno', { variant: 'success' });
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba smazání', { variant: 'error' }),
  });

  const toggleActive = (it: PenaltyCatalogItem) => {
    updateMutation.mutate({
      id: it.id,
      req: {
        disciplineId: it.disciplineId,
        code: it.code,
        label: it.label,
        seconds: it.seconds,
        category: it.category,
        sortOrder: it.sortOrder,
        isActive: !it.isActive,
      },
    });
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ alignItems: 'center' }}>
        Katalog trestných bodů per soutěž a disciplína. Pro PÚ CTIF se při vytvoření soutěže ze šablony
        automaticky doplní 16 položek dle Směrnice hry Plamen 2022. Položku můžete deaktivovat (přepínač
        vpravo) — pak nebude vidět při zápisu výsledků.
      </Alert>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <TextField
          select
          size="small"
          label="Filtr disciplíny"
          value={disciplineFilter}
          onChange={(e) => setDisciplineFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="all">Všechny disciplíny</MenuItem>
          {allDisciplines.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              {d.categoryName} — {d.name}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
          Nová položka
        </Button>
      </Stack>

      {ctifDisciplines.length === 0 && (
        <Alert severity="warning">
          Tato soutěž nemá žádnou disciplínu typu „Požární útok CTIF" — default seed není relevantní.
        </Alert>
      )}

      {filtered.length === 0 && !listQuery.isLoading && (
        <Card>
          <CardContent>
            <Typography color="text.secondary">Žádné položky katalogu.</Typography>
          </CardContent>
        </Card>
      )}

      {CATEGORY_ORDER.map((cat) => {
        const arr = grouped.get(cat) ?? [];
        if (arr.length === 0) return null;
        return (
          <Card key={cat}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                {PENALTY_CATEGORY_LABELS[cat]} <Typography component="span" variant="caption" color="text.secondary">({arr.length})</Typography>
              </Typography>
              <Stack spacing={0.5}>
                {arr
                  .slice()
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((it) => (
                    <Stack
                      key={it.id}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        opacity: it.isActive ? 1 : 0.55,
                        bgcolor: it.isActive ? 'transparent' : 'action.hover',
                      }}
                    >
                      <Chip size="small" label={`${it.seconds}s`} color="warning" variant="outlined" sx={{ fontFamily: 'monospace', minWidth: 56 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {it.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {it.code} · {it.disciplineId == null ? 'všechny disciplíny' : disciplineLabel(allDisciplines, it.disciplineId)}
                        </Typography>
                      </Box>
                      <Tooltip title={it.isActive ? 'Aktivní — vidí zápisovatel' : 'Vypnuto — skryto v zápisu'}>
                        <Switch
                          size="small"
                          checked={it.isActive}
                          onChange={() => toggleActive(it)}
                          disabled={updateMutation.isPending}
                        />
                      </Tooltip>
                      <IconButton size="small" onClick={() => setEditing(it)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          if (confirm(`Smazat položku „${it.label}"? Existující výsledky zachovají svůj snapshot.`)) {
                            deleteMutation.mutate(it.id);
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
              </Stack>
            </CardContent>
          </Card>
        );
      })}

      {/* Edit dialog */}
      <PenaltyCatalogDialog
        open={!!editing || creating}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        item={editing}
        disciplines={allDisciplines}
        loading={updateMutation.isPending || createMutation.isPending}
        onSubmit={(req) => {
          if (editing) updateMutation.mutate({ id: editing.id, req });
          else createMutation.mutate(req);
        }}
      />
    </Stack>
  );
};

const disciplineLabel = (
  list: { id: number; name: string; categoryName: string }[],
  id: number
): string => {
  const d = list.find((x) => x.id === id);
  return d ? `${d.categoryName} — ${d.name}` : `disciplína #${id}`;
};

interface DialogProps {
  open: boolean;
  item: PenaltyCatalogItem | null;
  disciplines: { id: number; name: string; categoryName: string }[];
  loading: boolean;
  onClose: () => void;
  onSubmit: (req: SavePenaltyCatalogRequest) => void;
}

const PenaltyCatalogDialog = ({ open, item, disciplines, loading, onClose, onSubmit }: DialogProps) => {
  const [form, setForm] = useState<SavePenaltyCatalogRequest>({
    disciplineId: null,
    code: '',
    label: '',
    seconds: 10,
    category: 'CUSTOM',
    sortOrder: 0,
    isActive: true,
  });

  // Reset on open
  useMemo(() => {
    if (!open) return;
    if (item) {
      setForm({
        disciplineId: item.disciplineId,
        code: item.code,
        label: item.label,
        seconds: item.seconds,
        category: item.category,
        sortOrder: item.sortOrder,
        isActive: item.isActive,
      });
    } else {
      setForm({
        disciplineId: null,
        code: '',
        label: '',
        seconds: 10,
        category: 'CUSTOM',
        sortOrder: 9000,
        isActive: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  const isValid = form.code.trim().length > 0 && form.label.trim().length > 0 && form.seconds > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{item ? 'Úprava položky katalogu' : 'Nová položka katalogu'}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <TextField
            label="Kód"
            size="small"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            disabled={!!item}
            helperText={item ? 'Kód existující položky nelze měnit' : 'Unikátní kód v rámci disciplíny'}
          />
          <TextField
            label="Popis"
            size="small"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            fullWidth
          />
          <TextField
            label="Sekundy"
            type="number"
            size="small"
            value={form.seconds}
            onChange={(e) => setForm({ ...form, seconds: Math.max(0, Number(e.target.value) || 0) })}
            inputProps={{ min: 0, max: 999 }}
          />
          <TextField
            select
            label="Kategorie"
            size="small"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value as PenaltyCategory })}
          >
            {CATEGORY_ORDER.map((c) => (
              <MenuItem key={c} value={c}>
                {PENALTY_CATEGORY_LABELS[c]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Disciplína"
            size="small"
            value={form.disciplineId ?? 'all'}
            onChange={(e) => setForm({ ...form, disciplineId: e.target.value === 'all' ? null : Number(e.target.value) })}
          >
            <MenuItem value="all">Všechny disciplíny soutěže</MenuItem>
            {disciplines.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.categoryName} — {d.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Pořadí (sort_order)"
            type="number"
            size="small"
            value={form.sortOrder ?? 0}
            onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) || 0 })}
          />
          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              size="small"
              checked={form.isActive ?? true}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            <Typography variant="body2">Aktivní (vidí zápisovatel)</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zrušit</Button>
        <Button variant="contained" onClick={() => onSubmit(form)} disabled={!isValid || loading}>
          {loading ? 'Ukládám…' : 'Uložit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
